import React, { useCallback, useContext, useMemo } from 'react';
import { DocumentCarouselContext, IActionCriteria } from '../../components/document/DocumentProvider';
import { useMutation, useQuery } from '@apollo/client';
import {
  AlertType,
  IDispatchInstruction,
  IDispatchInstructionDocumentCarouselQueryResult,
  IDispatchInstructionDocumentCarouselQueryVariables,
  IDispatchLabelFormatCountsQueryResult,
  IDispatchLabelFormatCountsQueryVariables,
  IDispatchLabelInput,
  IPrintDispatchLabelsFromActionCriteriaMutationResult,
  IPrintDispatchLabelsFromActionCriteriaMutationVariables,
  LabelFormat,
  ReportOutputType,
} from '../../graphql/schema/p4types';
import {
  IDocumentValues,
  useActionFilters,
  useDocumentCarouselStyles,
} from '../../components/document/documentCarouselUtil';
import DocumentCarouselHeader from '../../components/document/DocumentCarouselHeader';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import DocumentCarouselOutput from '../../components/document/DocumentCarouselOutput';
import DocumentCarouselButtons from '../../components/document/DocumentCarouselButtons';
import getClientMutationId from '../../utils/getClientMutationId';
import { useCookies } from 'react-cookie';
import { DocumentCarouselHeaderInfo } from '../../components/document/DocumentCarouselHeaderInfo';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import DocumentCarouselSkeleton from '../../components/document/DocumentCarouselSkeleton';
import DispatchInstructions from '../../graphql/entities/sales/DispatchInstructions';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import Dropdown from '../../components/connectedForm/Dropdown';
import Fieldset from '../../components/connectedForm/Fieldset';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useCheckPopUpBlockerFunction } from '../../utils/CheckPopUpBlockerFunction';
import { useP4ToastController } from '../system/NotificationsArea';
// Query/Mutation Imports
import DispatchLabelFormatCounts from '../../graphql/queries/sales/DispatchLabelFormatCounts.graphql';
import DispatchInstructionDocumentCarouselQuery from '../../graphql/queries/document/DispatchInstructionDocumentCarouselQuery.graphql';
import PrintDispatchLabelsFromActionCriteria from '../../graphql/mutations/sales/PrintDispatchLabelsFromActionCriteria.graphql';

//Cookies
const DISPATCH_LABEL_SETTINGS = 'dispatchLabelSettings';

const DispatchLabel = { ...DispatchInstructions, title: 'Dispatch Labels' };

type IDispatchLabelDocumentValues = IDocumentValues & {
  labelFormat: LabelFormat;
  selectedPrinterType: ReportOutputType;
};

const DispatchLabelFormat = ({ uniqueLabelData }: { uniqueLabelData: IDispatchLabelFormatCountsQueryResult }) => {
  const { watch, setValue } = useFormContext<IDispatchLabelDocumentValues>();

  const labelFormat = watch('labelFormat');

  const styles = useDocumentCarouselStyles();

  const supportedFormats = useMemo(() => {
    return uniqueLabelData?.getLabelFormatCounts?.map((labelFormatCount) => labelFormatCount.labelFormat) ?? [];
  }, [uniqueLabelData?.getLabelFormatCounts]);

  return (
    <div className={styles.row}>
      <Fieldset cols={1} style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Dropdown
          options={fieldMetaData.LabelFormat.enumValues.filter((value) => {
            return supportedFormats.includes(value.value);
          })}
          label="Label Format"
          onChange={(newLabelFormat: LabelFormat) => {
            setValue('labelFormat', newLabelFormat, {
              shouldTouch: true,
              shouldDirty: true,
            });
          }}
          value={labelFormat}
        />
      </Fieldset>
    </div>
  );
};

/**
 * Render the content for the Dispatch Instruction Document Carousel.
 * All querying should be completed before rendering this element so that 'documentDefaults' are accurate/up-to-date when the user see's them.
 * @param documentDefaults - Default document values. Should be final on initial load.
 * @param actionCriteria - The action criteria driving this particular print.
 * @param count - How many records are in this print job.
 * @param singleRecipient - Flag to indicate if all records are a single Customer/Supplier.
 * @param singlePrint - Flag to indicate if we're printing a single record.
 * @param singlePrintNode - If we're printing a single record, this should be the document-queried subset of that record for display/defaulting.
 *                        - Note - Will still be populated for non-single, will be arbitrarily the first record matching the criteria.
 * @param setCookie - Pass-through of cookie setter, used to update 'showSummary' and 'printContiguous' cookie values.
 * @param documentPrint - Print action object - Provides additional detail such as whether this is a reprint, print completion callback or additional email configuration.
 */
const DispatchLabelDocumentContent = ({
  documentDefaults,
  actionCriteria,
  count,
  singleRecipient,
  singlePrint,
  singlePrintNode,
  setCookie,
}: {
  documentDefaults: Partial<IDispatchLabelDocumentValues>;
  actionCriteria: IActionCriteria;
  count: number;
  singleRecipient: boolean;
  singlePrint: boolean;
  singlePrintNode: IDispatchInstruction;
  setCookie: ReturnType<typeof useCookies>[1];
}) => {
  const { dispatchSimpleToast } = useP4ToastController();
  const { onSkip } = useContext(DocumentCarouselContext);
  const styles = useDocumentCarouselStyles();

  //Use react-hook-form to manage our form values for better typing and performance than ConnectedForm.
  const methods = useForm<IDispatchLabelDocumentValues>({
    defaultValues: documentDefaults,
  });
  const { getValues } = methods;

  const handleQueryError = useComponentQueryErrorHandling();
  const [printDispatchLabels] = useMutation<
    IPrintDispatchLabelsFromActionCriteriaMutationResult,
    IPrintDispatchLabelsFromActionCriteriaMutationVariables
  >(PrintDispatchLabelsFromActionCriteria, {
    onError: handleQueryError,
  });

  const checkPopUpBlockerFunction = useCheckPopUpBlockerFunction();

  const onPrintClick = useCallback(() => {
    const { selectedPrinterType, printerId, labelFormat } = getValues();

    setCookie(
      DISPATCH_LABEL_SETTINGS,
      {
        selectedPrinterType: selectedPrinterType,
        systemPrinterId: printerId,
        labelFormat: labelFormat,
      },
      { years: 1 }
    );

    printDispatchLabels({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          actionCriteria: {
            filters: actionCriteria.filters,
            actionEntities: normalizeVariables(actionCriteria.actionEntities),
            include: actionCriteria.include,
          },
          labelPrintRequest: normalizeVariables({
            printerType: selectedPrinterType?.value,
            systemPrinterId: printerId,
            labelFormat: labelFormat?.value,
          }),
        },
      },
    })
      .then((response) => {
        let systemPrinterLabelCount = 0;
        for (const dispatchInstruction of response?.data?.printDispatchLabelsFromActionCriteria.result) {
          const labels: Array<IDispatchLabelInput> = [];
          for (const dispatchLabel of dispatchInstruction?.dispatchLabels as Array<IDispatchLabelInput>) {
            if (dispatchLabel.labelFormat === labelFormat?.value) {
              labels.push(dispatchLabel);
            }
          }

          // if selected printer is local, open the file in new tab
          if (selectedPrinterType?.value === 'LOCAL') {
            for (const label of labels) {
              const newWindow = checkPopUpBlockerFunction(`/api/resources/label/${label.fileName}`, '_blank');

              if (newWindow) {
                newWindow.print();
              }
            }
          } else if (labels?.length > 0) {
            systemPrinterLabelCount++;
          } else {
            dispatchSimpleToast(
              'Label Print Error',
              `No label available for dispatch instruction ${dispatchInstruction.documentNumber}`,
              AlertType.Warning
            );
          }
        }
        if (systemPrinterLabelCount > 0) {
          dispatchSimpleToast(
            'Label Printed',
            `Labels for ${systemPrinterLabelCount} dispatch instructions were sent to a system printer`,
            AlertType.Success
          );
        }
      })
      .finally(() => {
        onSkip(true);
      });
  }, [
    actionCriteria.actionEntities,
    actionCriteria.filters,
    actionCriteria.include,
    checkPopUpBlockerFunction,
    dispatchSimpleToast,
    getValues,
    onSkip,
    printDispatchLabels,
    setCookie,
  ]);

  const { data: uniqueLabelData } = useQuery<
    IDispatchLabelFormatCountsQueryResult,
    IDispatchLabelFormatCountsQueryVariables
  >(DispatchLabelFormatCounts, {
    variables: {
      actionCriteria: actionCriteria,
    },
  });

  const uniqueLabel = useMemo(() => {
    if (!uniqueLabelData?.getLabelFormatCounts) {
      return '';
    }

    return uniqueLabelData.getLabelFormatCounts
      .map((labelFormatCount) => {
        return `${labelFormatCount!.labelFormat}: ${labelFormatCount!.count}`;
      })
      .join(', ');
  }, [uniqueLabelData]);

  return (
    <FormProvider {...methods}>
      <DocumentCarouselHeader
        entity={DispatchLabel}
        count={count}
        secondaryTitle={
          <DocumentCarouselHeaderInfo
            contentLeft={singlePrint ? `Sales Order ${singlePrintNode?.salesOrder?.orderNumber}` : undefined}
            contentRight={
              singleRecipient ? `Customer ${singlePrintNode?.salesOrder?.customer?.name ?? 'Unknown'}` : undefined
            }
          />
        }
        reprint={false}
      />
      <div style={{ padding: '1em' }}>
        <DocumentCarouselOutput
          singleRecipient={true /* We always want to override the output type, so act as if this is always true */}
          outputTypes={fieldMetaData.DocumentOutputType.enumValues.filter((value) =>
            [ReportOutputType.LocalPrinter, ReportOutputType.SystemPrinter].includes(value.value)
          )}
        />
        <div className={styles.row}>
          <Fieldset cols={1} style={{ paddingLeft: 0, paddingRight: 0 }}>
            <BlurTextField value={uniqueLabel} readOnly={true} label="Labels" data-testid={'label-print-count'} />
          </Fieldset>
        </div>
        <DispatchLabelFormat uniqueLabelData={uniqueLabelData} />
      </div>
      <DocumentCarouselButtons onPrint={onPrintClick} />
    </FormProvider>
  );
};

const useDispatchLabelDocumentDefaults = ({
  cookies,
}: {
  cookies: ReturnType<typeof useCookies>[0];
}): Partial<IDispatchLabelDocumentValues> => {
  const { selectedPrinterType, systemPrinterId, labelFormat } = cookies[DISPATCH_LABEL_SETTINGS] || {
    selectedPrinterType: null,
    systemPrinterId: null,
    labelFormat: null,
  };

  return useMemo(() => {
    const printerId =
      systemPrinterId !== null && systemPrinterId !== undefined ? parseInt(systemPrinterId, 10) : undefined;

    const defaultValues: Partial<IDispatchLabelDocumentValues> & { labelFormat: boolean } = {
      overrideOutput: true,
      outputType: selectedPrinterType,
      printerId: printerId,
      labelFormat: labelFormat,
    };
    return defaultValues;
  }, [labelFormat, selectedPrinterType, systemPrinterId]);
};

/**
 * @param actionCriteria - The action criteria driving this particular print.
 * @param documentPrint - Print action object - Provides additional detail such as whether this is a reprint, print completion callback or additional email configuration.
 */
export const DispatchLabelDocumentCarouselCard = ({ actionCriteria }: { actionCriteria: IActionCriteria }) => {
  const filters = useActionFilters(actionCriteria);
  const { data: dispatchInstructionData, loading: dispatchInstructionLoading } = useQuery<
    IDispatchInstructionDocumentCarouselQueryResult,
    IDispatchInstructionDocumentCarouselQueryVariables
  >(DispatchInstructionDocumentCarouselQuery, {
    variables: {
      filters: filters,
      first: 2,
      aggregateCriteria: {
        groupColumns: ['salesOrder.customer.id'],
        sumColumns: ['id'],
      },
    },
  });

  const singlePrint: boolean = actionCriteria.include && actionCriteria.actionEntities.length === 1;
  //If the grouping gives a single Customer in the result, we can pull the document defaults off the Customer record and use it for the whole print.
  //(If there's >1 Customer, we cannot do this).
  const singleRecipient = dispatchInstructionData?.listData?.aggregateRows?.length === 1;
  //Note - Take care to always check 'singleRecipient' before consuming this node - singleRecipient *must* be true if consuming this node to avoid bugs.
  //       (This is because for multiple recipients, each node likely has different defaults and we'd only load the 'first' nodes defaults if we use this, potentially overriding the other values with them).
  const singlePrintNode = dispatchInstructionData?.listData?.edges?.[0]?.node as Maybe<IDispatchInstruction>;

  const [cookies, setCookie] = useCookies([DISPATCH_LABEL_SETTINGS]);
  const documentDefaults = useDispatchLabelDocumentDefaults({
    cookies,
  });

  if (dispatchInstructionData && !dispatchInstructionLoading) {
    return (
      <DispatchLabelDocumentContent
        documentDefaults={documentDefaults}
        setCookie={setCookie}
        actionCriteria={actionCriteria}
        count={dispatchInstructionData?.listData?.count ?? 0}
        singleRecipient={singleRecipient}
        singlePrint={singlePrint}
        singlePrintNode={singlePrintNode}
      />
    );
  }

  return <DocumentCarouselSkeleton />;
};
